import {isUndefined, omitBy} from 'lodash';

import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {
  createObjectPropertyUpdater,
  createResourceMapValueUpdater,
  createUpdater,
} from '../../../../srcCommon/helpers/updateHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {
  CalendarEventVersionReminder,
  serializeCalendarEventVersionReminder,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarTeammateAccessRolesEnum} from '../../../models/api/calendarModel';
import {CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {
  ApiCalendarVersionDataGroup,
  ApiCalendarVersionPageDataGroup,
  DataGroupModel,
} from '../../../models/api/dataGroupModel';
import {fetchApiAsync, patchApiAsync, postAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {findCalendarVersionLock} from '../../../services/locks/resourceLocks';
import {ThunkAsyncAction} from '../../appThunk';
import {optimisticUpdateAsyncAction} from '../../optimisticUpdates/optimisticUpdatesActions';
import {getCompanyUrl} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';
import {getOriginalData} from '../dataSelectors';
import {DataState} from '../dataState';
import {getUserNamespace} from '../teammates/teammatesSelectors';

/*
 * Action types.
 */

export enum CalendarVersionsActionsEnum {
  ALL_CALENDAR_VERSIONS_UPDATED = '@@data/calendarVersions/ALL_CALENDAR_VERSIONS_UPDATED',
}

export interface AllCalendarVersionsUpdatedAction extends ActionWithPayload<DataGroupModel> {
  type: CalendarVersionsActionsEnum.ALL_CALENDAR_VERSIONS_UPDATED;
}

interface CalendarPatch {
  teammateAccessRole?: CalendarTeammateAccessRolesEnum;
}
export interface CalendarVersionPatch {
  isSelected?: boolean;
  isHidden?: boolean;
  color?: PaletteColorsEnum;
  nameOverride?: string;
  reminders?: ReadonlyArray<CalendarEventVersionReminder>;
  calendar?: CalendarPatch;
}

export function updateCalendarVersionAction(
  calendarVersionId: number,
  patch: CalendarVersionPatch,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const optimisticUpdater = createObjectPropertyUpdater<DataState, 'calendarVersions'>(
      'calendarVersions',
      createResourceMapValueUpdater(
        calendarVersionId,
        createUpdater<CalendarVersionModel>((calendarVersion) => ({
          ...calendarVersion,
          ...patch,
        })),
      ),
    );

    await dispatch(
      optimisticUpdateAsyncAction(optimisticUpdater, async () =>
        findCalendarVersionLock(calendarVersionId).lockAsync(async () => {
          const state = getState();
          const {calendarVersions} = getOriginalData(state);
          const existingCalendarVersion = calendarVersions[calendarVersionId];
          if (!existingCalendarVersion) {
            throw new Error('A calendar version with this ID could not be found.');
          }

          const updatedCalendarVersion = {
            ...existingCalendarVersion,
            ...patch,
          };

          try {
            const body = serializeCalendarVersionPatch(patch);
            const calendarVersionDataGroup = await patchApiAsync(
              ApiCalendarVersionDataGroup,
              updatedCalendarVersion.url,
              body,
            );

            return updateDataAction(calendarVersionDataGroup);
          } catch (error) {
            if (!(error instanceof HttpError)) {
              reportToBugsnag(error);
            }
            throw error;
          }
        }),
      ),
    );
  };
}

function serializeCalendarVersionPatch(patch: CalendarVersionPatch) {
  const calendarUpdates = {
    teammate_access_role: patch.calendar ? patch.calendar.teammateAccessRole : undefined,
  };

  const updates = {
    selected: patch.isSelected,
    hidden: patch.isHidden,
    color: patch.color,
    name_override: patch.nameOverride,
    reminders: patch.reminders ? patch.reminders.map(serializeCalendarEventVersionReminder) : undefined,
    calendar: omitBy(calendarUpdates, isUndefined),
  };

  return omitBy(updates, isUndefined);
}

export function updateAllCalendarVersionsAction(
  calendarVersions: DataGroupModel,
): AllCalendarVersionsUpdatedAction {
  return {
    type: CalendarVersionsActionsEnum.ALL_CALENDAR_VERSIONS_UPDATED,
    payload: calendarVersions,
  };
}

export function fetchCalendarVersionsAsyncAction(namespace: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const url = `${companyUrl}/namespaces/${namespace}/calendar_versions`;
    const calendarVersions = await fetchApiAsync(ApiCalendarVersionPageDataGroup, url);

    dispatch(updateAllCalendarVersionsAction(calendarVersions));
  };
}

export function fetchCalendarVersionAsyncAction(id: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const url = `${companyUrl}/calendar_versions/${id}`;
    const calendarVersion = await fetchApiAsync(ApiCalendarVersionDataGroup, url);

    dispatch(updateDataAction(calendarVersion));
  };
}

export function calendarVersionForcedOnceTimeSyncAction(id: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const companyUrl = getCompanyUrl(state);
    const namespace = getUserNamespace(state);
    const url = `${companyUrl}/namespaces/${encodeURIComponent(
      namespace,
    )}/calendar_versions/${encodeURIComponent(id)}/force_sync`;
    await postAsync(url, {});
  };
}

/*
 * Union type.
 */

export type CalendarVersionsAction = AllCalendarVersionsUpdatedAction;
